import { useEffect, useCallback } from 'react'
import { __ } from '@wordpress/i18n'

/**
 * Custom hook for WooCommerce product selection validation
 *
 * Provides shared validation logic for WooCommerce product/category/tag selection,
 * including zero reviews warning detection.
 *
 * @param {Object} params
 * @param {Array} params.selectedProducts - Currently selected products
 * @param {Array} params.selectedCategories - Currently selected categories
 * @param {Array} params.selectedTags - Currently selected tags
 * @param {Function} params.setValidationError - State setter for validation error
 * @param {boolean} params.enabled - Whether to enable automatic validation (default: true)
 * @returns {Object} Validation utilities
 */
const useWooValidation = ( {
    selectedProducts = [],
    selectedCategories = [],
    selectedTags = [],
    setValidationError,
    enabled = true
} ) => {

    /**
     * Check if at least one item is selected (product, category, or tag)
     */
    const hasAnySelection = useCallback( () => {
        return selectedProducts.length > 0 || selectedCategories.length > 0 || selectedTags.length > 0;
    }, [ selectedProducts, selectedCategories, selectedTags ] );

    /**
     * Calculate total reviews across all selections
     */
    const getTotalSelectedReviews = useCallback( () => {
        const productReviews = selectedProducts.reduce( ( sum, product ) => sum + ( product.review_count || 0 ), 0 );
        const categoryReviews = selectedCategories.reduce( ( sum, cat ) => sum + ( cat.review_count || 0 ), 0 );
        const tagReviews = selectedTags.reduce( ( sum, tag ) => sum + ( tag.review_count || 0 ), 0 );
        return productReviews + categoryReviews + tagReviews;
    }, [ selectedProducts, selectedCategories, selectedTags ] );

    /**
     * Validate selection and return error message if invalid
     * @returns {string|null} Error message or null if valid
     */
    const validateSelection = useCallback( () => {
        if ( ! hasAnySelection() ) {
            return __( 'You need to at least select 1 product', 'sb-customizer' );
        }
        return null;
    }, [ hasAnySelection ] );

    /**
     * Get zero reviews warning message if applicable
     * @returns {string|null} Warning message or null
     */
    const getZeroReviewsWarning = useCallback( () => {
        if ( hasAnySelection() && getTotalSelectedReviews() === 0 ) {
            return __( 'Your selection has no reviews.', 'sb-customizer' );
        }
        return null;
    }, [ hasAnySelection, getTotalSelectedReviews ] );

    // Auto-update validation error when selection changes (zero reviews warning)
    useEffect( () => {
        if ( ! setValidationError || ! enabled ) return;

        const hasSelection = hasAnySelection();
        const totalReviews = getTotalSelectedReviews();

        if ( hasSelection && totalReviews === 0 ) {
            setValidationError( __( 'Your selection has no reviews.', 'sb-customizer' ) );
        } else if ( hasSelection && totalReviews > 0 ) {
            // Clear warning when reviews are available
            setValidationError( null );
        }
    }, [ selectedProducts, selectedCategories, selectedTags, hasAnySelection, getTotalSelectedReviews, setValidationError, enabled ] );

    return {
        hasAnySelection,
        getTotalSelectedReviews,
        validateSelection,
        getZeroReviewsWarning
    };
};

export default useWooValidation;
